import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Match } from '../api/client';
import { TeamBadge } from './TeamBadge';
import { formatTime, countdown, weekdayShort, dayNum, monthShort } from '../lib/format';
import { venue, COUNTRY_ISO } from '../lib/venues';
import { useStore } from '../stores/store';

const Card = styled.button<{ $fav: boolean }>`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 8px;
  text-align: left;
  background: ${({ theme }) => theme.colors.surface};
  border: 1.5px solid ${({ theme, $fav }) => ($fav ? theme.colors.gold : theme.colors.borderStrong)};
  border-radius: ${({ theme }) => theme.radius};
  padding: 12px 14px;
  margin-bottom: 10px;
  ${({ $fav, theme }) => $fav && `box-shadow: 0 0 0 1px ${theme.colors.gold} inset;`}
`;

const Mid = styled.div`
  text-align: center;
  min-width: 92px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
`;

const ScoreBox = styled.div`
  font-size: 1.6rem;
  font-weight: 800;
  letter-spacing: 1px;
`;

// Chip con estética calendario: día de semana arriba, número grande, mes.
const DateChip = styled.div`
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  line-height: 1.05;
  background: ${({ theme }) => theme.colors.surfaceAlt};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  padding: 3px 9px;
  .wd { font-size: 0.62rem; text-transform: uppercase; letter-spacing: 0.5px; color: ${({ theme }) => theme.colors.textMuted}; }
  .dn { font-size: 1.15rem; font-weight: 800; }
  .mo { font-size: 0.62rem; text-transform: uppercase; color: ${({ theme }) => theme.colors.textMuted}; }
`;

const Kick = styled.div`
  font-size: 0.9rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
`;

const Sub = styled.div`
  font-size: 0.78rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const Live = styled.div`
  font-size: 0.68rem;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.live};
  display: flex;
  align-items: center;
  gap: 4px;
  justify-content: center;
  &::before {
    content: '';
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.live};
    animation: pulse 1.2s infinite;
  }
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.3; }
  }
`;

const Foot = styled.div`
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.text};
  border-top: 1px dashed ${({ theme }) => theme.colors.border};
  padding-top: 8px;
  margin-top: 4px;

  img {
    width: 24px;
    height: 17px;
    border-radius: 3px;
    object-fit: cover;
    box-shadow: 0 0 0 1px ${({ theme }) => theme.colors.border};
  }
`;

// ¿El favorito juega este partido? Considera tanto el equipo real como la proyección.
function involvesFavorite(m: Match, favorite: string | null): boolean {
  if (!favorite) return false;
  const a = m.team1.resolved ?? m.team1.name;
  const b = m.team2.resolved ?? m.team2.name;
  return a === favorite || b === favorite;
}

export function MatchCard({ m, showVenue = true }: { m: Match; showVenue?: boolean }) {
  const navigate = useNavigate();
  const favorite = useStore((s) => s.favorite);
  const v = showVenue ? venue(m.ground) : null;

  return (
    <Card $fav={involvesFavorite(m, favorite)} onClick={() => navigate(`/match/${m.id}`)}>
      <TeamBadge team={m.team1} align="left" />

      <Mid>
        {m.score ? (
          <ScoreBox>
            {m.score.ft[0]}–{m.score.ft[1]}
          </ScoreBox>
        ) : (
          <DateChip>
            <span className="wd">{weekdayShort(m.kickoffUtc)}</span>
            <span className="dn">{dayNum(m.kickoffUtc)}</span>
            <span className="mo">{monthShort(m.kickoffUtc)}</span>
          </DateChip>
        )}
        <Kick>{formatTime(m.kickoffUtc)}</Kick>
        {m.status === 'live' && <Live>EN JUEGO{m.live?.clock ? ` · ${m.live.clock}` : ''}</Live>}
        {m.status === 'upcoming' && <Sub>{countdown(m.kickoffUtc)}</Sub>}
      </Mid>

      <TeamBadge team={m.team2} align="right" />

      {v && (
        <Foot>
          <img src={`https://flagcdn.com/w160/${COUNTRY_ISO[v.country]}.png`} alt={v.country} loading="lazy" />
          {v.stadium} · {v.city}
        </Foot>
      )}
    </Card>
  );
}
